import { ref } from 'vue'
import { createConsumer, type Consumer, type Subscription } from '@rails/actioncable'
import { CHATWOOT_CABLE_URL } from '@/api/chatwoot'
import type { AuthUser } from '@/types/auth'

// ── Fase 1 do plano de tempo real (docs/planos/PLANO-REALTIME-ACTIONCABLE.md) ──
// Módulo de conexão isolado: abre e mantém o WebSocket do Chatwoot, expõe os
// eventos brutos via onChatwootEvent(). Deliberadamente NÃO faz nenhuma
// tradução evento → estado da dashboard ainda — isso é escopo da Fase 2.
//
// Formato do identifier/canal e do evento confirmados ao vivo na Fase 0:
// canal "RoomChannel" precisa de pubsub_token + user_id + account_id (sem
// user_id o servidor trata como conexão de contato/widget, não confirma nem
// rejeita); cada evento chega como { event: "message.updated", data: {...} }.

export type ChatwootRealtimeStatus = 'idle' | 'connecting' | 'connected' | 'disconnected'

export interface ChatwootRealtimeEvent {
  event: string
  data: unknown
}

export const realtimeStatus = ref<ChatwootRealtimeStatus>('idle')

type Listener = (evt: ChatwootRealtimeEvent) => void
const listeners = new Set<Listener>()

/** Registra um listener pros eventos brutos. Devolve a função de cancelamento. */
export function onChatwootEvent(cb: Listener): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

let consumer: Consumer | null = null
let subscription: Subscription | null = null

/**
 * Abre a conexão pro agente logado. Idempotente — chamar de novo com o
 * consumer já aberto não faz nada (mesmo padrão de startDashboardEngine()).
 * Reconexão com backoff já vem de graça do @rails/actioncable (ConnectionMonitor
 * interno) — não precisa reimplementar isso aqui.
 */
export function startChatwootRealtime(auth: AuthUser): void {
  if (consumer) return
  if (!auth?.pubsub_token) {
    // Sessão persistida antes desta feature existir (localStorage antigo) —
    // só resolve com um novo login. Não é erro, não precisa alarmar o usuário.
    console.warn('[ChatwootRealtime] sem pubsub_token no usuário logado — faça login de novo pra ativar tempo real.')
    return
  }

  realtimeStatus.value = 'connecting'
  consumer = createConsumer(CHATWOOT_CABLE_URL)

  subscription = consumer.subscriptions.create(
    {
      channel: 'RoomChannel',
      pubsub_token: auth.pubsub_token,
      user_id: auth.id,
      account_id: auth.account_id,
    },
    {
      connected() {
        realtimeStatus.value = 'connected'
      },
      disconnected() {
        realtimeStatus.value = 'disconnected'
      },
      rejected() {
        console.error('[ChatwootRealtime] inscrição rejeitada pelo Chatwoot — pubsub_token/user_id/account_id inválidos.')
        realtimeStatus.value = 'disconnected'
      },
      received(payload: { event?: string; data?: unknown } | unknown) {
        const p = (payload ?? {}) as { event?: string; data?: unknown }
        const evt: ChatwootRealtimeEvent = { event: p.event ?? 'unknown', data: p.data ?? payload }
        // Log bruto de todo evento recebido (independente de virar patch ou
        // não) — visibilidade de diagnóstico, útil pra capturar tipos de
        // evento ainda não mapeados em src/services/chatwootRealtimeTranslator.ts.
        console.debug('[ChatwootRealtime] evento:', evt.event, evt.data)
        listeners.forEach(cb => cb(evt))
      },
    },
  )
}

/** Fecha a conexão (logout). */
export function stopChatwootRealtime(): void {
  subscription?.unsubscribe()
  consumer?.disconnect()
  subscription = null
  consumer = null
  realtimeStatus.value = 'idle'
}
